import { monthOf, weekStartOf } from '@wiztime/core';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { dayPath, monthPath, STATS_PATH, weekPath } from '../app/routes';
import { useMonthUi } from '../features/month/monthUiStore';
import { useLayoutMode, LayoutMode } from '../hooks/useMediaQuery';
import { useRouteView, useToday, ViewKind } from '../hooks/useRouteView';
import { cx } from '../lib/cx';
import { viewDate } from './viewDate';
import { useViewUi } from './viewUiStore';
import styles from './TopBar.module.css';

/**
 * Segmented control: day / week / month / stats. Day, week and month keep the date being
 * viewed; the week and the month hand the day view back the day it last showed, unless the
 * month grid has a focused day (2026-10b §9.6).
 */
export function ViewSwitcher() {
  const { t } = useTranslation();
  const view = useRouteView();
  const today = useToday();
  const focusedDate = useMonthUi((state) => state.focusedDate);
  const lastDayDate = useViewUi((state) => state.lastDayDate);
  const narrow = useLayoutMode() === LayoutMode.Narrow;
  const date = viewDate(view, today, focusedDate, lastDayDate);
  const items = [
    {
      kind: ViewKind.Day,
      to: dayPath(date),
      label: narrow ? t('nav.dayShort') : t('nav.day'),
    },
    {
      kind: ViewKind.Week,
      to: weekPath(weekStartOf(date)),
      label: narrow ? t('nav.weekShort') : t('nav.week'),
    },
    {
      kind: ViewKind.Month,
      to: monthPath(monthOf(date)),
      label: narrow ? t('nav.monthShort') : t('nav.month'),
    },
    { kind: ViewKind.Stats, to: STATS_PATH, label: narrow ? t('nav.statsShort') : t('nav.stats') },
  ];
  return (
    <nav className={styles.segmented} aria-label={t('nav.views')}>
      {items.map((item) => (
        <Link
          key={item.kind}
          to={item.to}
          className={cx(styles.segment, view.kind === item.kind && styles.segmentActive)}
          aria-current={view.kind === item.kind ? 'page' : undefined}
        >
          {item.label}
        </Link>
      ))}
    </nav>
  );
}
